import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Wallet, PiggyBank } from "lucide-react";
import { Slider } from "@/components/ui/slider";

const formatINR = (v) => {
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
};

export const SipCalculator = () => {
  const [monthly, setMonthly] = useState(10000);
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(12);

  const { invested, gains, total, gainPct } = useMemo(() => {
    const i = rate / 12 / 100;
    const n = years * 12;
    const fv = monthly * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
    const inv = monthly * n;
    const g = fv - inv;
    return { invested: inv, gains: g, total: fv, gainPct: Math.min((g / fv) * 100, 100) };
  }, [monthly, years, rate]);

  const controls = [
    { label: "Monthly investment", value: monthly, set: setMonthly, min: 500, max: 100000, step: 500, display: `₹${monthly.toLocaleString("en-IN")}`, testid: "sip-monthly-slider" },
    { label: "Time period", value: years, set: setYears, min: 1, max: 30, step: 1, display: `${years} years`, testid: "sip-years-slider" },
    { label: "Expected return (p.a.)", value: rate, set: setRate, min: 1, max: 20, step: 0.5, display: `${rate}%`, testid: "sip-rate-slider" },
  ];

  return (
    <section id="calculator" className="py-20 lg:py-32 bg-white border-y border-border scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-bronze">SIP Calculator</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-forest">
            See your money grow, year after year
          </h2>
          <p className="mt-4 text-base text-muted-foreground">
            Move the sliders and watch what a disciplined monthly SIP can become. Then talk to us to make it real.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="sip-calculator"
          className="mt-12 grid lg:grid-cols-2 gap-8"
        >
          <div className="bg-bone border border-border rounded-2xl p-6 lg:p-10 space-y-10">
            {controls.map((c) => (
              <div key={c.label}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-muted-foreground">{c.label}</span>
                  <span data-testid={`${c.testid}-value`} className="font-serif text-xl font-medium text-forest">{c.display}</span>
                </div>
                <Slider
                  data-testid={c.testid}
                  value={[c.value]}
                  onValueChange={([v]) => c.set(v)}
                  min={c.min}
                  max={c.max}
                  step={c.step}
                  className="[&_[role=slider]]:bg-forest [&_[role=slider]]:border-forest [&_.bg-primary]:bg-bronze"
                />
              </div>
            ))}
          </div>

          <div className="bg-forest rounded-2xl p-6 lg:p-10 text-bone flex flex-col justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-bronze-light">Your wealth after {years} years</p>
              <p data-testid="sip-total-value" className="mt-3 font-serif text-5xl lg:text-6xl font-medium">{formatINR(total)}</p>
            </div>

            <div className="mt-8">
              <div className="flex h-3 rounded-full overflow-hidden bg-bone/10" data-testid="sip-breakup-bar">
                <div className="bg-bone/60 transition-[width] duration-500" style={{ width: `${100 - gainPct}%` }} />
                <div className="bg-bronze-light transition-[width] duration-500" style={{ width: `${gainPct}%` }} />
              </div>
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-3 text-sm text-bone/80">
                    <Wallet className="h-4 w-4 text-bone/60" /> You invest
                  </span>
                  <span data-testid="sip-invested-value" className="font-semibold">{formatINR(invested)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-3 text-sm text-bone/80">
                    <TrendingUp className="h-4 w-4 text-bronze-light" /> Your money earns
                  </span>
                  <span data-testid="sip-gains-value" className="font-semibold text-bronze-light">{formatINR(gains)}</span>
                </div>
              </div>
            </div>

            <a
              href="#contact"
              data-testid="sip-cta-btn"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-bronze px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
            >
              <PiggyBank className="h-4 w-4" />
              Start this SIP — talk to Rajkiran
            </a>
            <p className="mt-4 text-xs text-bone/50">
              Illustration only. Mutual fund returns are not guaranteed and depend on market performance.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
